/**
 * NEXUS UI - RangeSlider
 * Futuristic slider input with value visualization
 */

import React, { useState, useRef, useCallback } from 'react';
import type { BaseProps, NexusVariant, NexusSize } from './types';

export interface RangeSliderProps extends BaseProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  variant?: NexusVariant;
  size?: NexusSize;
  label?: string;
  showValue?: boolean;
  showMinMax?: boolean;
  showTicks?: boolean;
  tickCount?: number;
  unit?: string;
  disabled?: boolean;
  formatValue?: (value: number) => string;
}

const variantColors: Record<NexusVariant, { base: string; glow: string }> = {
  cyan: { base: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  blue: { base: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)' },
  violet: { base: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)' },
  green: { base: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  amber: { base: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  red: { base: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
};

const sizeConfig: Record<NexusSize, { trackHeight: number; thumbSize: number }> = {
  sm: { trackHeight: 4, thumbSize: 14 },
  md: { trackHeight: 6, thumbSize: 18 },
  lg: { trackHeight: 8, thumbSize: 22 },
  xl: { trackHeight: 10, thumbSize: 26 },
};

export const RangeSlider: React.FC<RangeSliderProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  variant = 'cyan',
  size = 'md',
  label,
  showValue = true,
  showMinMax = true,
  showTicks = false,
  tickCount = 5,
  unit = '',
  disabled = false,
  formatValue,
  className = '',
  style,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const colors = variantColors[variant];
  const config = sizeConfig[size];
  const percentage = ((value - min) / (max - min)) * 100;

  const displayValue = formatValue ? formatValue(value) : `${value}${unit}`;

  const updateValue = useCallback((clientX: number) => {
    if (!trackRef.current || disabled) return;

    const rect = trackRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const rawValue = (x / rect.width) * (max - min) + min;
    const steppedValue = Math.round(rawValue / step) * step;
    const clampedValue = Math.max(min, Math.min(max, steppedValue));

    onChange(clampedValue);
  }, [min, max, step, onChange, disabled]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    setIsDragging(true);
    updateValue(e.clientX);

    const handleMouseMove = (e: MouseEvent) => updateValue(e.clientX);
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    let newValue = value;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      newValue = Math.min(max, value + step);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      newValue = Math.max(min, value - step);
    } else if (e.key === 'Home') {
      newValue = min;
    } else if (e.key === 'End') {
      newValue = max;
    }

    if (newValue !== value) {
      e.preventDefault();
      onChange(newValue);
    }
  };

  const ticks = showTicks
    ? Array.from({ length: tickCount }, (_, i) => min + ((max - min) * i) / (tickCount - 1))
    : [];

  return (
    <div
      className={`nx-range-slider ${className}`}
      style={{
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {/* Header */}
      {(label || showValue) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px',
          }}
        >
          {label && (
            <span
              style={{
                fontFamily: 'var(--nx-font-interface)',
                fontSize: '12px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--nx-text-secondary)',
              }}
            >
              {label}
            </span>
          )}
          {showValue && (
            <span
              style={{
                fontFamily: 'var(--nx-font-mono)',
                fontSize: '14px',
                fontWeight: 600,
                color: colors.base,
                textShadow: isDragging ? `0 0 8px ${colors.glow}` : 'none',
                transition: 'text-shadow 0.15s ease-out',
              }}
            >
              {displayValue}
            </span>
          )}
        </div>
      )}

      {/* Slider */}
      <div
        ref={trackRef}
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        tabIndex={disabled ? -1 : 0}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onKeyDown={handleKeyDown}
        style={{
          position: 'relative',
          height: config.thumbSize + 8,
          cursor: disabled ? 'not-allowed' : 'pointer',
          outline: 'none',
          touchAction: 'none',
        }}
      >
        {/* Track background */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
            height: config.trackHeight,
            background: 'var(--nx-void-elevated)',
            borderRadius: config.trackHeight / 2,
          }}
        />

        {/* Track fill */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            transform: 'translateY(-50%)',
            width: `${percentage}%`,
            height: config.trackHeight,
            background: `linear-gradient(90deg, ${colors.base}80, ${colors.base})`,
            borderRadius: config.trackHeight / 2,
            boxShadow: `0 0 8px ${colors.glow}`,
            transition: isDragging ? 'none' : 'width 0.1s ease-out',
          }}
        />

        {/* Thumb */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `${percentage}%`,
            transform: 'translate(-50%, -50%)',
            width: config.thumbSize,
            height: config.thumbSize,
            borderRadius: '50%',
            background: 'var(--nx-void-surface)',
            border: `2px solid ${colors.base}`,
            boxShadow: isDragging || isHovered
              ? `0 0 12px ${colors.glow}, 0 0 24px ${colors.glow}`
              : `0 0 8px ${colors.glow}`,
            transition: isDragging ? 'none' : 'left 0.1s ease-out, box-shadow 0.15s ease-out',
          }}
        >
          {/* Inner glow */}
          <div
            style={{
              position: 'absolute',
              inset: 3,
              borderRadius: '50%',
              background: colors.base,
              opacity: isDragging ? 0.8 : isHovered ? 0.5 : 0.3,
              transition: 'opacity 0.15s ease-out',
            }}
          />
        </div>

        {/* Ticks */}
        {showTicks && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              transform: `translateY(${config.trackHeight + 8}px)`,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {ticks.map((tick, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    width: 1,
                    height: 4,
                    background: 'var(--nx-text-ghost)',
                  }}
                />
                <span
                  style={{
                    fontFamily: 'var(--nx-font-mono)',
                    fontSize: '9px',
                    color: 'var(--nx-text-ghost)',
                    marginTop: '2px',
                  }}
                >
                  {formatValue ? formatValue(tick) : tick}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Min/Max labels */}
      {showMinMax && !showTicks && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '4px',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--nx-font-mono)',
              fontSize: '10px',
              color: 'var(--nx-text-ghost)',
            }}
          >
            {formatValue ? formatValue(min) : `${min}${unit}`}
          </span>
          <span
            style={{
              fontFamily: 'var(--nx-font-mono)',
              fontSize: '10px',
              color: 'var(--nx-text-ghost)',
            }}
          >
            {formatValue ? formatValue(max) : `${max}${unit}`}
          </span>
        </div>
      )}
    </div>
  );
};

export default RangeSlider;
